import { Check, CircleDashed, Loader2, XCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { AutoSaveStatus } from '../hooks/useAutoSave';

/** Statuses that represent a save outcome and get a visible icon + label. */
export type AutoSaveOutcomeStatus = Extract<
  AutoSaveStatus,
  'saving' | 'blocked' | 'saved' | 'error'
>;

export interface AutoSaveStatusDisplay {
  Icon: LucideIcon;
  label: string;
  textClass: string;
  spin?: boolean;
}

/**
 * Single source of truth for the icon/label/color treatment of save
 * outcomes, shared by AutoSavePill, AutoSaveStatusIndicator, and
 * AutoSaveComments so wording stays consistent across pages.
 */
export const AUTO_SAVE_STATUS_DISPLAY: Record<
  AutoSaveOutcomeStatus,
  AutoSaveStatusDisplay
> = {
  saving: {
    Icon: Loader2,
    label: 'Saving...',
    textClass: 'text-muted-foreground',
    spin: true,
  },
  // A save the form held back (invalid/incomplete or user-declined). Quiet and
  // non-alarming — the inline field error carries the "why"; this just keeps
  // the indicator honest instead of going blank or claiming "Saved".
  blocked: {
    Icon: CircleDashed,
    label: 'Not saved',
    textClass: 'text-muted-foreground',
  },
  saved: { Icon: Check, label: 'Saved', textClass: 'text-green-600' },
  error: { Icon: XCircle, label: 'Save failed', textClass: 'text-red-500' },
};

export const getAutoSaveStatusDisplay = (
  status: AutoSaveStatus
): AutoSaveStatusDisplay | null =>
  status === 'saving' ||
  status === 'blocked' ||
  status === 'saved' ||
  status === 'error'
    ? AUTO_SAVE_STATUS_DISPLAY[status]
    : null;
